// supervisor-loop-attempt-start.ts
//
// Split out of supervisor-loop.ts (was 205 lines after an earlier split,
// still over the 200-line budget — review 260909-p6-m2 INFO). Owns the FIRST
// journal write of one control-loop tick: compute run_id + input_hash (D08,
// dogfood vòng 4 Bug D fix — the controller's field, computed from data it
// already holds before the worker ever sees the prompt) and commit
// ticket_state_changed(in_progress) + attempt_started together.
import { randomUUID } from "node:crypto";
import { promises as fs } from "node:fs";
import { join } from "node:path";
import { computeAttemptInputHash } from "./canonical-json-and-checksum.js";
import { formatAttemptStartLine } from "./run-progress-lines.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import type { SupervisorTickInput } from "./supervisor-loop-types.js";

export interface JournalAttemptStartInput {
  tickInput: Pick<SupervisorTickInput, "store" | "progress" | "now">;
  ticket: Ticket;
  attemptNo: number;
  prompt: string;
}

export interface JournalAttemptStartResult {
  runId: string;
}

export async function journalAttemptStart(
  input: JournalAttemptStartInput,
): Promise<JournalAttemptStartResult> {
  const { tickInput, ticket, attemptNo, prompt } = input;
  const { store } = tickInput;
  const runId = randomUUID();
  const inputHash = computeAttemptInputHash({
    run_id: runId,
    ticket_id: ticket.id,
    ticket_revision: ticket.revision,
    prompt,
  });

  await store.commit([
    {
      event_id: randomUUID(),
      delta: { kind: "ticket_state_changed", ticket: ticket.id, status: "in_progress" },
    },
    {
      event_id: randomUUID(),
      delta: { kind: "attempt_started", ticket: ticket.id, attemptNo, runId, inputHash },
    },
  ]);

  // BUG-3 fix (dogfood 260925 luồng 5): `max_sessions_per_hour` reads its
  // sliding-window data from `attempts/<ticketId>-<attemptNo>/` mtimes
  // (supervisor-attempt-directory-reader.ts's readAttemptTimestampsIso), but
  // that directory used to be created ONLY by attempt-forensics.ts on
  // contract_error/recovering — a clean attempt left no directory, so the
  // cap was permanently a no-op (its own KNOWN LIMITATION comment). This is
  // the ONE call site that fires exactly once per dispatch regardless of
  // outcome, so it eagerly creates the (empty) marker dir here — mkdir is
  // idempotent, so a LATER forensics archival into the same path (a
  // subsequent contract_error for this same attempt) is unaffected.
  await fs.mkdir(join(store.controlRoot, "attempts", `${ticket.id}-${attemptNo}`), {
    recursive: true,
  });

  // Req 2 (R-15): ▶ printed AFTER attempt_started is committed — the journal
  // write is the source of truth, the console line is a courtesy mirror.
  const now = tickInput.now ?? (() => new Date());
  tickInput.progress?.(formatAttemptStartLine(ticket.id, ticket.kind, attemptNo, now()));

  return { runId };
}
